import { useMemo, useState } from 'react'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Skeleton from '@mui/material/Skeleton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'

import type { RequestLogRow } from '@server/domain/request-log'

import CustomChip from '@core/components/mui/chip'
import CustomTextField from '@core/components/mui/text-field'
import type { ThemeColor } from '@core/types'
import AppLink from '@components/link'
import { TableDefault } from '@components/table-default'
import { KpiCard } from '@components/due-diligence/kpi-card'
import { CertificateHistoryDialog } from '@components/due-diligence/certificate-history-dialog'

import { useTRPC } from '@/lib/trpc'
import { requireAdmin } from '@/lib/route-guards'
import { formatBrl, formatDateTime } from '@/lib/due-diligence'

const search = z.object({
  days: z.coerce.number().int().min(1).max(365).default(30),
  status: z.enum(['all', 'done', 'failed', 'canceled', 'running', 'queued']).default('all'),
  source: z.string().default('all'),
  q: z.string().default('')
})

type Search = z.infer<typeof search>

export const Route = createFileRoute('/_dashboard/admin/audit')({
  validateSearch: search,
  beforeLoad: requireAdmin,
  component: Audit
})

const STATUS: Record<string, { label: string; color: ThemeColor }> = {
  done: { label: 'Concluída', color: 'success' },
  failed: { label: 'Falhou', color: 'error' },
  canceled: { label: 'Cancelada', color: 'warning' },
  queued: { label: 'Na fila', color: 'info' },
  running: { label: 'Rodando', color: 'info' }
}

const WINDOWS = [
  { value: 7, label: 'Últimos 7 dias' },
  { value: 30, label: 'Últimos 30 dias' },
  { value: 90, label: 'Últimos 90 dias' },
  { value: 365, label: 'Último ano' }
]

const STATUS_FILTER = [
  { value: 'all', label: 'Todos os resultados' },
  { value: 'done', label: 'Concluídas' },
  { value: 'failed', label: 'Falhas' },
  { value: 'canceled', label: 'Canceladas' },
  { value: 'running', label: 'Rodando' },
  { value: 'queued', label: 'Na fila' }
]

const column = createColumnHelper<RequestLogRow>()

/** Every certificate request the house made — provider, outcome and cost, newest first. */
function Audit() {
  const trpc = useTRPC()
  const { days, status, source, q } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const [history, setHistory] = useState<{ id: string; name: string; at: Date } | null>(null)

  const patch = (values: Partial<Search>) => navigate({ search: prev => ({ ...prev, ...values }) })

  // Window, result and provider go to the server so the table reaches past the
  // capped page; the KPIs come from the window's own totals, not these rows, so
  // they stay true behind the cap. Only the free-text search filters here.
  const audit = useQuery(
    trpc.operations.requests.queryOptions({
      days,
      status: status === 'all' ? undefined : status,
      source: source === 'all' ? undefined : source
    })
  )
  const view = audit.data
  const windowRows = useMemo(() => view?.rows ?? [], [view])

  const sources = view?.sources ?? []

  const needle = q.trim().toLowerCase()
  const rows = useMemo(
    () =>
      windowRows.filter(
        r =>
          !needle || [r.caseName, r.subjectName, r.certificateName].some(v => v.toLowerCase().includes(needle))
      ),
    [windowRows, needle]
  )

  const total = view?.total ?? 0
  const done = view?.counts.done ?? 0
  const failed = view?.counts.failed ?? 0
  // The table only holds the most recent slice; say so when it is clipped.
  const capped = windowRows.length >= 300

  const columns = useMemo(
    () => [
      column.accessor('at', {
        header: 'Quando',
        cell: ({ getValue }) => (
          <Typography variant='body2' className='whitespace-nowrap'>
            {formatDateTime(getValue())}
          </Typography>
        )
      }),
      column.accessor('caseName', {
        header: 'Caso',
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <Typography
              variant='body2'
              component={AppLink}
              href={`/diligences/${row.original.diligenceId}`}
              className='font-medium no-underline text-textPrimary hover:text-primary'
            >
              {row.original.caseName}
            </Typography>
            <Typography variant='caption' color='text.disabled'>
              {row.original.subjectName}
            </Typography>
          </div>
        )
      }),
      column.accessor('certificateName', {
        header: 'Certidão',
        cell: ({ getValue }) => <Typography variant='body2'>{getValue()}</Typography>
      }),
      column.accessor('source', {
        header: 'Fornecedor',
        cell: ({ getValue }) => (
          <Typography variant='body2' className='whitespace-nowrap'>
            {getValue()}
          </Typography>
        )
      }),
      column.accessor('status', {
        header: 'Resultado',
        cell: ({ getValue }) => {
          const value = getValue()
          const meta = STATUS[value] ?? { label: value, color: 'secondary' as ThemeColor }

          return <CustomChip size='small' variant='tonal' color={meta.color} label={meta.label} />
        }
      }),
      column.accessor('costBrl', {
        header: 'Custo',
        cell: ({ getValue }) => {
          const value = getValue()

          return (
            <Typography variant='body2' className='tabular-nums whitespace-nowrap' color={value ? 'text.primary' : 'text.disabled'}>
              {value !== null ? formatBrl(value) : '—'}
            </Typography>
          )
        }
      }),
      column.accessor('error', {
        header: 'Resposta',
        enableSorting: false,
        cell: ({ getValue }) => (
          <Typography variant='body2' color='text.secondary' className='line-clamp-2 max-is-[320px]'>
            {getValue() ?? '—'}
          </Typography>
        )
      }),
      column.display({
        id: 'actions',
        header: 'Ações',
        cell: ({ row }) => (
          <div className='flex items-center gap-1'>
            {row.original.status === 'done' && (
              <Tooltip title='Abrir certidão'>
                <IconButton
                  size='small'
                  component='a'
                  href={`/files/certificates/${row.original.certificateId}`}
                  target='_blank'
                  rel='noreferrer'
                >
                  <i className='tabler-file-text text-[20px]' />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title='Histórico de tentativas'>
              <IconButton
                size='small'
                onClick={() =>
                  setHistory({ id: row.original.certificateId, name: row.original.certificateName, at: row.original.at })
                }
              >
                <i className='tabler-history text-[20px]' />
              </IconButton>
            </Tooltip>
          </div>
        )
      })
    ],
    []
  )

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 15 }, sorting: [{ id: 'at', desc: true }] }
  })

  return (
    <div className='flex flex-col gap-6'>
      <Grid container spacing={6}>
        <Grid size={{ xs: 6, md: 3 }}>
          <KpiCard
            title='Requisições'
            value={total.toLocaleString('pt-BR')}
            caption='tentativas no período (cada retry conta)'
            icon='tabler-list-search'
            color='primary'
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <KpiCard
            title='Concluídas'
            value={done.toLocaleString('pt-BR')}
            caption='voltaram com documento'
            icon='tabler-circle-check'
            color='success'
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <KpiCard
            title='Falhas'
            value={failed.toLocaleString('pt-BR')}
            caption='erro ou recusa do órgão'
            icon='tabler-alert-triangle'
            color='error'
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <KpiCard
            title='Cobrado'
            value={formatBrl(view?.totalBilledBrl ?? 0)}
            caption='todas as chamadas pagas do período'
            icon='tabler-cash'
            color='warning'
          />
        </Grid>
      </Grid>

      <Card>
        <CardHeader
          title='Auditoria de requisições'
          subheader='Toda vez que o sistema pediu uma certidão a um fornecedor — com o resultado, o erro e o custo.'
          action={
            <Button
              variant='tonal'
              color='secondary'
              size='small'
              startIcon={<i className='tabler-refresh' />}
              onClick={() => audit.refetch()}
              disabled={audit.isFetching}
            >
              Atualizar
            </Button>
          }
        />
        <CardContent className='flex flex-col gap-4'>
          <div className='flex flex-wrap items-center gap-3'>
            <CustomTextField
              select
              label='Período'
              value={days}
              onChange={event => patch({ days: Number(event.target.value) })}
              className='min-is-[160px]'
            >
              {WINDOWS.map(w => (
                <MenuItem key={w.value} value={w.value}>
                  {w.label}
                </MenuItem>
              ))}
            </CustomTextField>

            <CustomTextField
              select
              label='Resultado'
              value={status}
              onChange={event => patch({ status: event.target.value as Search['status'] })}
              className='min-is-[170px]'
            >
              {STATUS_FILTER.map(s => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </CustomTextField>

            <CustomTextField
              select
              label='Fornecedor'
              value={sources.includes(source) ? source : 'all'}
              onChange={event => patch({ source: event.target.value })}
              className='min-is-[160px]'
            >
              <MenuItem value='all'>Todos os fornecedores</MenuItem>
              {sources.map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </CustomTextField>

            <CustomTextField
              label='Buscar'
              placeholder='Caso, pessoa ou certidão'
              value={q}
              onChange={event => patch({ q: event.target.value })}
              className='min-is-[220px]'
            />

            {(status !== 'all' || source !== 'all' || q) && (
              <Button
                variant='text'
                color='secondary'
                size='small'
                onClick={() => patch({ status: 'all', source: 'all', q: '' })}
              >
                Limpar filtros
              </Button>
            )}

            <Typography variant='body2' color='text.secondary' className='mis-auto'>
              {total.toLocaleString('pt-BR')} requisições no período
            </Typography>
          </div>

          {audit.isError && (
            <Alert severity='error'>Não foi possível carregar a auditoria. Tente atualizar em instantes.</Alert>
          )}

          {audit.isPending ? (
            <div className='flex flex-col gap-2'>
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} variant='rounded' height={44} />
              ))}
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <TableDefault
                table={table}
                withPagination
                emptyMessage='Nenhuma requisição no período escolhido.'
                onRowClick={row =>
                  setHistory({ id: row.original.certificateId, name: row.original.certificateName, at: row.original.at })
                }
              />
            </div>
          )}

          {capped && (
            <Typography variant='caption' color='text.disabled'>
              Mostrando as 300 requisições mais recentes do recorte. Use o filtro de resultado ou fornecedor para
              alcançar as demais.
            </Typography>
          )}
        </CardContent>
      </Card>

      {history && (
        <CertificateHistoryDialog
          certificateId={history.id}
          name={history.name}
          highlightAt={history.at}
          open={Boolean(history)}
          onClose={() => setHistory(null)}
        />
      )}
    </div>
  )
}
